// Onglet « Mail ».
//
// Le groupe `mail` existait en base depuis la migration 004, il était
// validé par un schéma zod et utilisé à chaque envoi — mais il n'avait
// pas d'écran. Modifier l'objet ou le corps du message demandait donc
// un `UPDATE` SQL. Ce n'était pas bloquant pour la démonstration, le
// modèle par défaut étant correct, mais ça ne pouvait pas rester ainsi
// à la remise : personne ne livre un produit dont un texte client ne se
// change qu'en base.
//
// ── Quatre marques, et seulement quatre ──
//
// `{{reference}}`, `{{client}}`, `{{projet}}`, `{{validite}}`. Ce n'est
// pas un moteur de gabarit : pas de logique, pas d'évaluation, pas
// d'accès à un objet arbitraire. Une marque inconnue reste visible
// telle quelle dans le mail plutôt que d'être remplacée par du vide —
// un trou silencieux dans un message client est pire qu'une marque
// visible qui se remarque à la relecture.

import { useCallback, useEffect, useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import { TextField, Textarea } from '../../ui/TextField.jsx';
import Button from '../../ui/Button.jsx';
import { ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useToast } from '../../ui/Toast.jsx';
import { useResource } from '../../lib/useResource.js';
import { api } from '../../lib/api.js';

/** Les seules marques reconnues, avec ce qu'elles valent à l'envoi. */
const MARQUES = [
  { clef: '{{reference}}', quoi: 'la référence du devis' },
  { clef: '{{client}}', quoi: 'la société du client' },
  { clef: '{{projet}}', quoi: 'le projet, s’il est renseigné' },
  { clef: '{{validite}}', quoi: 'la durée de validité, en jours' },
];

const CONNUES = MARQUES.map((m) => m.clef);

/** Les marques écrites dans un texte qui ne seront jamais remplacées. */
export function marquesInconnues(texte) {
  return [...new Set(String(texte ?? '').match(/\{\{[^}]*\}\}/g) ?? [])].filter(
    (m) => !CONNUES.includes(m),
  );
}

export function MailPanel({ isAdmin }) {
  const toast = useToast();
  const load = useCallback(() => api.get('/parametres'), []);
  const { data, error, loading, reload } = useResource(load);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (data) setForm(data.settings.mail ?? { subject: '', body: '', cc: null, reply_to: null });
  }, [data]);

  if (loading || !form) return <SkeletonRows rows={5} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const set = (patch) => setForm({ ...form, ...patch });
  const inconnues = [
    ...new Set([...marquesInconnues(form.subject), ...marquesInconnues(form.body)]),
  ];

  async function save(event) {
    event.preventDefault();
    setPending(true);
    setErrors({});
    try {
      await api.put('/parametres/mail', {
        subject: form.subject,
        body: form.body,
        cc: form.cc || null,
        reply_to: form.reply_to || null,
      });
      toast.success('Modèle d’e-mail enregistré.');
      reload();
    } catch (err) {
      if (err.details) setErrors(err.details);
      else toast.error(err.message);
    } finally {
      setPending(false);
    }
  }

  return (
    <Card>
      <CardHead title="Modèle d’e-mail d’envoi">
        {isAdmin ? (
          <Button size="sm" variant="primary" type="submit" form="form-mail" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        ) : null}
      </CardHead>
      <CardBody>
        <form id="form-mail" className="stack" onSubmit={save}>
          <p className="field__hint">
            Ce texte accompagne le PDF du devis. Ce qui engage commercialement figure sur le
            document, pas dans le corps du message : celui-ci reste court et ne promet rien.
          </p>

          <div className="mail-marques">
            <p className="caps">Marques reconnues</p>
            <ul>
              {MARQUES.map((m) => (
                <li key={m.clef}>
                  <code>{m.clef}</code> — {m.quoi}
                </li>
              ))}
            </ul>
            <p className="field__hint">
              Toute autre marque reste écrite telle quelle dans le mail. Ce n’est pas un moteur de
              gabarit : aucune logique, aucun accès à autre chose que ces quatre valeurs.
            </p>
          </div>

          {inconnues.length > 0 ? (
            <div className="warnings" role="alert">
              <p className="caps">{inconnues.length} marque(s) non reconnue(s)</p>
              <p>
                {inconnues.join(', ')} — elles partiront <strong>telles quelles</strong> chez le
                client. Corrigez-les, ou retirez-les.
              </p>
            </div>
          ) : null}

          <TextField
            label="Objet"
            required
            value={form.subject ?? ''}
            error={errors.subject}
            disabled={!isAdmin}
            onChange={(e) => set({ subject: e.target.value })}
          />
          <Textarea
            label="Corps du message"
            required
            rows={10}
            value={form.body ?? ''}
            error={errors.body}
            disabled={!isAdmin}
            onChange={(e) => set({ body: e.target.value })}
          />
          <div className="grid-2">
            <TextField
              label="Copie systématique"
              value={form.cc ?? ''}
              error={errors.cc}
              disabled={!isAdmin}
              hint="Typiquement la boîte de l’atelier. Laisser vide si personne."
              onChange={(e) => set({ cc: e.target.value })}
            />
            <TextField
              label="Répondre à"
              value={form.reply_to ?? ''}
              error={errors.reply_to}
              disabled={!isAdmin}
              hint="Vide : les réponses arrivent sur l’adresse d’envoi."
              onChange={(e) => set({ reply_to: e.target.value })}
            />
          </div>

          <div className="mail-apercu">
            <p className="caps">Aperçu avec des valeurs d’exemple</p>
            <p className="mail-apercu__objet">
              {apercu(form.subject)}
            </p>
            <pre className="mail-apercu__corps">{apercu(form.body)}</pre>
            {!isAdmin ? (
              <p className="field__hint">
                Lecture seule : la modification du modèle est réservée aux administrateurs.
              </p>
            ) : null}
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

/**
 * Aperçu local, avec des valeurs d'exemple.
 *
 * Il reproduit le remplacement du serveur — les mêmes quatre marques,
 * et rien d'autre — pour que ce qui s'affiche ici soit ce qui partira.
 * Un aperçu plus permissif que l'envoi mentirait.
 */
function apercu(texte) {
  return String(texte ?? '')
    .replaceAll('{{reference}}', '2026-08-014')
    .replaceAll('{{client}}', 'ACME SA')
    .replaceAll('{{projet}}', 'Bâti de convoyeur')
    .replaceAll('{{validite}}', '30');
}

export default MailPanel;
